import React from "react";
import { Modal, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
// react-pdf removed to avoid bundler/worker issues for cross-origin PDFs
// import "../css/DocumentViewerModal.css";

const DocumentViewerModal = ({ show, onHide, documentUrl, fileName, fileType }) => {

  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || '';
  };

  const isImage = (extension) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
  };

  const isVideo = (extension) => {
    return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension);
  };

  const isPdf = (extension) => {
    return extension === 'pdf';
  };

  const isAudio = (extension) => {
    return ['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension);
  };

  const extension = getFileExtension(fileName);
  const canPreview = isImage(extension) || isVideo(extension) || isPdf(extension) || isAudio(extension);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(documentUrl, '_blank', 'noopener,noreferrer');
  };

  const renderPreview = () => {
    if (!canPreview) {
      return (
        <div className="text-center p-4">
          <div className="mb-3">
            <FontAwesomeIcon icon={faExternalLinkAlt} size="3x" className="text-muted" />
          </div>
          <p className="text-muted">Preview not available for this file type</p>
          <p className="small text-muted">Click "Open in New Tab" to view the file</p>
        </div>
      );
    }

    if (isImage(extension)) {
      return (
        <div className="text-center">
          <img
            src={documentUrl}
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }} className="text-center p-4">
            <p className="text-muted">Failed to load image</p>
            <Button variant="outline-primary" size="sm" onClick={handleOpenInNewTab}>
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
              Open in New Tab
            </Button>
          </div>
        </div>
      );
    }

    if (isVideo(extension)) {
      return (
        <div className="text-center">
          <video
            controls
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            <source src={documentUrl} type={`video/${extension}`} />
            Your browser does not support the video tag.
          </video>
          <div style={{ display: 'none' }} className="text-center p-4">
            <p className="text-muted">Failed to load video</p>
            <Button variant="outline-primary" size="sm" onClick={handleOpenInNewTab}>
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
              Open in New Tab
            </Button>
          </div>
        </div>
      );
    }

    if (isAudio(extension)) {
      return (
        <div className="text-center p-4">
          <audio controls style={{ width: '100%' }}>
            <source src={documentUrl} type={`audio/${extension}`} />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    if (isPdf(extension)) {
      return (
        <div style={{ height: '70vh' }}>
          <iframe
            src={`https://docs.google.com/gview?url=${documentUrl}&embedded=true`}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={fileName}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div style={{ display: 'none' }} className="text-center p-4">
            <div className="mb-3">
              <i className="fas fa-file-pdf text-danger" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5 className="text-muted mb-3">PDF Preview Not Available</h5>
            <p className="text-muted mb-3">This PDF cannot be displayed inline due to security restrictions or file configuration.</p>
            <p className="small text-muted mb-4">Please use one of the options below to view the document:</p>
            <div className="d-flex gap-3 justify-content-center">
              <Button variant="primary" size="lg" onClick={handleOpenInNewTab}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Open in New Tab
              </Button>
              <Button variant="outline-secondary" size="lg" onClick={handleDownload}>
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="document-viewer-modal"
    >
      <Modal.Header closeButton className="d-flex justify-content-between align-items-center">
        <div className="flex-grow-1">
          <Modal.Title className="text-truncate me-3" style={{ maxWidth: '70%' }}>
            {fileName}
          </Modal.Title>
          {isPdf(getFileExtension(fileName)) && (
            <small className="text-muted d-block">
              PDF preview may not work due to security restrictions. Use "Open in New Tab" if needed.
            </small>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleDownload}
            title="Download"
          >
            <FontAwesomeIcon icon={faDownload} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleOpenInNewTab}
            title="Open in New Tab"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: '400px' }}>
        {renderPreview()}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentViewerModal;
